import { useState } from 'react'
import { ChevronDownIcon } from './ui'

const OPTIONS = [
  {
    value: 'Carport',
    label: 'Carport',
    description: 'Covered structure with roof but no walls, usually attached to your home.',
  },
  {
    value: 'Driveway',
    label: 'Driveway',
    description: 'Private parking area on your property, uncovered.',
  },
  {
    value: 'Garage',
    label: 'Garage',
    description: 'Enclosed, lockable structure attached to or separate from your home.',
  },
  {
    value: 'Street',
    label: 'Street',
    description: 'Public road parking outside your property.',
  },
  {
    value: 'Secured parking lot',
    label: 'Secured parking lot',
    description: 'Gated or access-controlled parking facility (e.g., apartment complex).',
  },
  {
    value: 'Unsecured parking lot',
    label: 'Unsecured parking lot',
    description:
      'A parking area, not residential. Unfenced. Covered or uncovered, with open access to the vehicle.',
  },
  {
    value: 'Storage',
    label: 'Storage',
    description: 'Designated storage facility. Can be covered or uncovered.',
  },
] as const

const OPTION_ORDER = new Map(OPTIONS.map((o, i) => [o.value, i]))

function sortExpandedOptions(
  options: (typeof OPTIONS)[number][],
  baseline: string | null
): (typeof OPTIONS)[number][] {
  if (options.length <= 1 || !baseline) {
    return [...options].sort((a, b) => (OPTION_ORDER.get(a.value) ?? 0) - (OPTION_ORDER.get(b.value) ?? 0))
  }
  const baselineIdx = options.findIndex((o) => o.value === baseline)
  if (baselineIdx <= 0) {
    return [...options].sort((a, b) => (OPTION_ORDER.get(a.value) ?? 0) - (OPTION_ORDER.get(b.value) ?? 0))
  }
  const baselineOpt = options[baselineIdx]
  const rest = options.filter((o) => o.value !== baseline)
  rest.sort((a, b) => (OPTION_ORDER.get(a.value) ?? 0) - (OPTION_ORDER.get(b.value) ?? 0))
  return [baselineOpt, ...rest]
}

function RadioIconSmall({ checked }: { checked: boolean }) {
  return (
    <div
      className={`relative shrink-0 size-[16px] rounded-full border border-solid ${
        checked ? 'bg-background-brand-solid border-background-brand-solid' : 'border-border-primary'
      }`}
    >
      {checked && <div className="absolute inset-[31.25%] bg-background-primary rounded-full" />}
    </div>
  )
}

export type OvernightParkingAccordionProps = {
  id: string
  value: string
  onChange: (next: string) => void
  /** Value loaded from policy — used for “Currently on your policy” on the matching option */
  baselineValue: string | null
}

/** True when the chosen value is not yet saved on the policy (e.g. yellow highlight on the control). */
export function isOvernightParkingUnsaved(value: string, baselineValue: string | null): boolean {
  return value.trim() !== '' && (baselineValue === null ? true : value !== baselineValue)
}

export default function OvernightParkingAccordion({ id, value, onChange, baselineValue }: OvernightParkingAccordionProps) {
  const [expanded, setExpanded] = useState(false)
  const selected = OPTIONS.find((o) => o.value === value)
  const hasSelection = Boolean(selected)
  const unsaved = isOvernightParkingUnsaved(value, baselineValue)

  /** Minimalist yellow band when unsaved (1px); default states keep 2px brand/neutral borders. */
  const triggerBorderClass = unsaved
    ? 'border border-yellow-500'
    : hasSelection
      ? 'border-2 border-brand-border'
      : 'border-2 border-border-primary'

  /** Avoid brand (teal) focus ring on top of yellow unsaved border — one accent only. */
  const triggerFocusRingClass = unsaved
    ? 'focus-visible:ring-2 focus-visible:ring-yellow-500/80 focus-visible:ring-offset-0'
    : 'focus-visible:ring-2 focus-visible:ring-brand-border focus-visible:ring-offset-0'

  return (
    <div className="flex flex-col gap-0 w-full">
      <button
        id={id}
        type="button"
        aria-expanded={expanded}
        aria-controls={`${id}-panel`}
        onClick={() => setExpanded((o) => !o)}
        className={`bg-background-primary flex gap-[12px] items-start justify-between px-[12px] py-[10px] rounded-lg w-full text-left transition-colors focus:outline-none ${triggerFocusRingClass} ${triggerBorderClass}`}
      >
        <div className="flex flex-1 flex-col gap-[2px] items-start min-w-0 pr-[8px]">
          {!selected ? (
            <p className="font-body font-semibold text-md leading-md text-text-primary-900">Select a location</p>
          ) : (
            <>
              <p className="font-body font-semibold text-md leading-md text-text-primary-900">
                {selected.label}
                {baselineValue !== null && selected.value === baselineValue && (
                  <span className="font-body font-semibold text-sm leading-sm text-brand-solid ml-[6px]">
                    — On your policy now
                  </span>
                )}
              </p>
              <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">{selected.description}</p>
            </>
          )}
        </div>
        <div className="flex items-center shrink-0 self-start pt-[2px]">
          <span
            className={`text-text-tertiary-600 transition-transform duration-[250ms] ease-in-out motion-reduce:transition-none ${
              expanded ? 'rotate-180' : 'rotate-0'
            }`}
            aria-hidden
          >
            <ChevronDownIcon className="w-4 h-4" />
          </span>
        </div>
      </button>

      <div
        id={`${id}-panel`}
        className={`grid w-full transition-[grid-template-rows] duration-[250ms] ease-in-out motion-reduce:transition-none ${
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          {expanded && (
            <div className="flex flex-col gap-[12px] items-start pt-[12px] w-full">
              {sortExpandedOptions(
                value ? [...OPTIONS.filter((o) => o.value !== value)] : [...OPTIONS],
                baselineValue
              ).map((option) => {
                const isSelected = value === option.value
                const isOnPolicy = baselineValue !== null && option.value === baselineValue
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value)
                      setExpanded(false)
                    }}
                    className={`border-2 flex gap-[12px] items-start justify-between p-[16px] relative rounded-xl shrink-0 w-full text-left transition-colors focus:outline-none focus:ring-2 focus:ring-brand-border ${
                      isOnPolicy
                        ? 'bg-background-tertiary border-brand-border shadow-xs'
                        : isSelected
                          ? 'bg-background-primary border-brand-border'
                          : 'bg-background-primary border-border-secondary'
                    }`}
                  >
                    <div className="flex flex-1 flex-col gap-[2px] items-start min-w-0 pr-[8px]">
                      <p className="font-body font-semibold text-md leading-md text-text-primary-900">
                        {option.label}
                        {isOnPolicy && (
                          <span className="font-body font-semibold text-sm leading-sm text-brand-solid ml-[6px]">
                            — On your policy now
                          </span>
                        )}
                      </p>
                      <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">{option.description}</p>
                    </div>
                    <div className="flex items-start justify-center shrink-0 pt-[2px]">
                      <RadioIconSmall checked={isSelected} />
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
